import { useSyncExternalStore } from 'react';

// ── Types ──────────────────────────────────────────────────────
export type FontSize = 'normal' | 'large' | 'xlarge';
export type ThemeMode = 'light' | 'dark' | 'high-contrast';
export type ClockStyle = 'digital' | 'analog';
export type ClockSize = 'small' | 'medium' | 'large';
export type ScreensaverMode = 'photos' | 'clock-only';
/**
 * T6.5 / D6 — reduce-motion preference.
 * - `auto`: respect the OS-level `prefers-reduced-motion` media query (default).
 * - `on`:   force-disable animations / transitions app-wide regardless of OS.
 * - `off`:  force-enable animations even when the OS would suppress them.
 *
 * The effective value is applied at App-root by setting
 * `<html data-reduce-motion="true|false">`; a CSS rule in `global.css`
 * then collapses animation / transition durations to 0.01ms.
 */
export type ReduceMotionPreference = 'auto' | 'on' | 'off';

export interface DisplaySettings {
  brightness: number;   // 0–100
  /**
   * T7.1 / E1 — intentionally **session-only** (not persisted to localStorage).
   *
   * Rationale: `sleepMode` is a transient runtime flag toggled by:
   *   - `SleepOverlay` (auto-engages after 1 minute of idle as fallback screensaver).
   *   - `DisplaySettingsPage` (manual "Sleep now" button → `setSleep(true)`).
   *   - `ScreensaverController` (force-wakes via `setSleep(false)` on activity).
   *
   * Persisting it across reloads would trap residents on a black screen if
   * the app reloaded while sleep was engaged (e.g. crash, OTA update, Electron
   * restart) — they'd power-cycle the device to recover. By design,
   * `loadFromStorage()` always returns `sleepMode: false` and `saveToStorage()`
   * never writes the field. The shape stays in the interface because three
   * consumer components subscribe to it via `useDisplaySettings()`; the field
   * has real readers, just no persistence.
   *
   * If you find yourself wanting to persist this, persist a separate
   * `sleepOnLaunch: boolean` preference instead — never the transient flag.
   */
  sleepMode: boolean;
  fontSize: FontSize;
  theme: ThemeMode;
  screensaverMode: ScreensaverMode;
  clockStyle: ClockStyle;
  clockSize: ClockSize;
  weatherLocation: string; // e.g. "Deinze,Belgium" — empty = auto from building
  /** T6.5 / D6 — user-controllable motion reduction. Persists in localStorage. */
  reduceMotion: ReduceMotionPreference;
}

const STORAGE_KEY = 'caro_display_settings';

const DEFAULT_SETTINGS: DisplaySettings = {
  brightness: 100,
  sleepMode: false,
  fontSize: 'normal',
  theme: 'light',
  screensaverMode: 'photos',
  clockStyle: 'digital',
  clockSize: 'medium',
  weatherLocation: '',
  reduceMotion: 'auto',
};

// ── Module-level store ─────────────────────────────────────────
let settings: DisplaySettings = { ...DEFAULT_SETTINGS };
const listeners = new Set<() => void>();

function loadFromStorage(): DisplaySettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const parsed = JSON.parse(raw);
    return {
      brightness: typeof parsed.brightness === 'number' ? Math.max(0, Math.min(100, parsed.brightness)) : 100,
      sleepMode: false, // Never persist sleep mode — always wake on reload
      fontSize: ['normal', 'large', 'xlarge'].includes(parsed.fontSize) ? parsed.fontSize : 'normal',
      theme: ['light', 'dark', 'high-contrast'].includes(parsed.theme) ? parsed.theme : 'light',
      screensaverMode: ['photos', 'clock-only'].includes(parsed.screensaverMode) ? parsed.screensaverMode : 'photos',
      clockStyle: ['digital', 'analog'].includes(parsed.clockStyle) ? parsed.clockStyle : 'digital',
      clockSize: ['small', 'medium', 'large'].includes(parsed.clockSize) ? parsed.clockSize : 'medium',
      weatherLocation: typeof parsed.weatherLocation === 'string' ? parsed.weatherLocation : '',
      reduceMotion: ['auto', 'on', 'off'].includes(parsed.reduceMotion) ? parsed.reduceMotion : 'auto',
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

function saveToStorage() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      brightness: settings.brightness,
      fontSize: settings.fontSize,
      theme: settings.theme,
      screensaverMode: settings.screensaverMode,
      clockStyle: settings.clockStyle,
      clockSize: settings.clockSize,
      weatherLocation: settings.weatherLocation,
      reduceMotion: settings.reduceMotion,
    }));
  } catch {
    // localStorage not available
  }
}

/**
 * Resolve the effective reduce-motion boolean from the tri-state preference.
 * `auto` reads the OS media query each time it's called; `on`/`off` are
 * explicit overrides. Returns `false` in non-browser environments (SSR/Jest)
 * where `matchMedia` is undefined.
 */
function resolveReduceMotion(pref: ReduceMotionPreference): boolean {
  if (pref === 'on') return true;
  if (pref === 'off') return false;
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function applyToDOM() {
  const root = document.documentElement;
  // Font size
  if (settings.fontSize === 'normal') {
    root.removeAttribute('data-font-size');
  } else {
    root.setAttribute('data-font-size', settings.fontSize);
  }
  // Theme
  if (settings.theme === 'light') {
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', settings.theme);
  }
  // T6.5 / D6: reduce-motion. We write the attribute unconditionally
  // (true | false) — even `auto` resolves to a concrete value at this
  // moment — so the CSS selector in global.css ("[data-reduce-motion='true']")
  // works without falling back to a `prefers-reduced-motion` media query.
  // The OS-level media query rule still exists for parity; the attribute
  // wins because it's more specific.
  root.setAttribute(
    'data-reduce-motion',
    resolveReduceMotion(settings.reduceMotion) ? 'true' : 'false',
  );
}

function notify() {
  listeners.forEach((fn) => fn());
}

function update(partial: Partial<DisplaySettings>) {
  settings = { ...settings, ...partial };
  applyToDOM();
  saveToStorage();
  notify();
}

// Initialize on module load
settings = loadFromStorage();
applyToDOM();

// T6.5 / D6: when the user picks `auto`, re-evaluate the OS-level
// `prefers-reduced-motion` query whenever it changes. The listener fires
// in both directions (true ↔ false). We only `applyToDOM()` — no state
// mutation — so subscribers don't get unnecessary re-renders when the
// settings object itself is unchanged.
if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
  try {
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => {
      if (settings.reduceMotion === 'auto') applyToDOM();
    };
    // Older Safari uses `addListener` / `removeListener`. The modern
    // `addEventListener('change', …)` is preferred where available.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
    } else if (typeof (mql as MediaQueryList & { addListener?: (cb: () => void) => void }).addListener === 'function') {
      (mql as MediaQueryList & { addListener: (cb: () => void) => void }).addListener(onChange);
    }
  } catch {
    // matchMedia not supported — auto degrades to "always off", per resolveReduceMotion.
  }
}

// ── Public API ─────────────────────────────────────────────────
function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => { listeners.delete(callback); };
}

function getSnapshot(): DisplaySettings {
  return settings;
}

export function useDisplaySettings() {
  const current = useSyncExternalStore(subscribe, getSnapshot);

  return {
    settings: current,

    setBrightness(value: number) {
      update({ brightness: Math.max(0, Math.min(100, Math.round(value))) });
    },

    toggleSleep() {
      update({ sleepMode: !settings.sleepMode });
    },

    setSleep(on: boolean) {
      update({ sleepMode: on });
    },

    setFontSize(size: FontSize) {
      update({ fontSize: size });
    },

    setTheme(theme: ThemeMode) {
      update({ theme });
    },

    setScreensaverMode(mode: ScreensaverMode) {
      update({ screensaverMode: mode });
    },

    setClockStyle(style: ClockStyle) {
      update({ clockStyle: style });
    },

    setClockSize(size: ClockSize) {
      update({ clockSize: size });
    },

    setWeatherLocation(location: string) {
      update({ weatherLocation: location.trim() });
    },

    /** T6.5 / D6 — three-state reduce-motion control. */
    setReduceMotion(pref: ReduceMotionPreference) {
      update({ reduceMotion: pref });
    },
  };
}
